import { useCallback } from "react";
import {
  searchWorkOrders,
  refetchWorkOrders,
  getRequiredItems,
} from "src/apis/pda1121Api";
import { PDA1121_R01Dto, PDA1121_R01SearchParams } from "./PDA1121_R01Dto";
import usePDA1121_R01Store from "./usePDA1121_R01Store";
import { useToast } from "src/hooks/useToast";

const PDA1121_R01Content = () => {
  const {
    searchParams,
    setOrderList,
    setSelectedOrder,
    setLoading,
    setShowList,
    setRequiredItems,
    resetDetail,
    requiredItems,
    itemCdInput,
    setItemCdInput,
    setScanResult,
  } = usePDA1121_R01Store();

  const { success, error, warning } = useToast();

  const buildParams = (): PDA1121_R01SearchParams => {
    const p: PDA1121_R01SearchParams = {};
    if (searchParams.productWorkNo) p.productWorkNo = searchParams.productWorkNo;
    if (searchParams.startDate) p.startDate = searchParams.startDate.split("T")[0];
    if (searchParams.endDate) p.endDate = searchParams.endDate.split("T")[0];
    return p;
  };

  const fetchOrders = useCallback(
    async (fn: (p: PDA1121_R01SearchParams) => Promise<unknown>) => {
      setLoading(true);
      try {
        const res = await fn(buildParams());
        const data = (res as { data?: { data?: PDA1121_R01Dto[] } })?.data?.data || [];
        setOrderList(data);
        if (data.length === 0) warning("조회된 공정지시가 없습니다.");
      } catch (err: unknown) {
        console.error("공정지시 조회 실패:", err);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "공정지시 조회 중 오류가 발생했습니다.";
        error(message);
        setOrderList([]);
      } finally {
        setLoading(false);
      }
    },
    [searchParams] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSearch = () => fetchOrders(searchWorkOrders);
  const handleRefetch = () => fetchOrders(refetchWorkOrders);

  /** 공정지시 선택 → 필요자재 적재 */
  const handleSelectOrder = useCallback(async (order: PDA1121_R01Dto) => {
    if (!order.productWorkNo) return;
    setLoading(true);
    try {
      setSelectedOrder(order);
      const res = await getRequiredItems(order.productWorkNo);
      const items = res?.data?.data || [];
      setRequiredItems(items);
      setScanResult(null);
      setItemCdInput("");
      if (items.length === 0) warning("등록된 필요자재(BOM)가 없습니다.");
      setShowList(false);
    } catch (err: unknown) {
      console.error("필요자재 조회 실패:", err);
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "필요자재 조회 중 오류가 발생했습니다.";
      error(message);
      setRequiredItems([]);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBackToList = () => {
    resetDetail();
    setShowList(true);
  };

  /**
   * itemCd 스캔 → 메모리 검증 (서버 호출 없음, 저장 없음)
   * 같은 itemCd의 모든 피더 행을 모아 표시
   */
  const handleScan = useCallback(
    (scanValue?: string) => {
      const code = (scanValue ?? itemCdInput).trim();
      if (!code) {
        warning("품목코드를 스캔하세요.");
        return;
      }

      const hits = requiredItems.filter((i) => i.itemCd === code);

      if (hits.length > 0) {
        const feeders = hits.map((h) => h.feederCd).filter(Boolean);
        setScanResult({
          itemCd: code,
          itemNm: hits[0].itemNm,
          itemSpec: hits[0].itemSpec,
          matched: true,
          feeders,
          message:
            feeders.length > 0
              ? `피더 ${feeders.join(", ")} 에 장착`
              : "일치 (피더 미지정)",
        });
        success(` ${hits[0].itemNm} 일치 (피더 ${feeders.length}곳)`);
      } else {
        setScanResult({
          itemCd: code,
          itemNm: "",
          itemSpec: "",
          matched: false,
          feeders: [],
          message: "이 공정에 필요한 자재가 아닙니다.",
        });
        error("❌ 이 공정 자재가 아닙니다.");
      }
      setItemCdInput("");
    },
    [itemCdInput, requiredItems] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    handleSearch,
    handleRefetch,
    handleSelectOrder,
    handleBackToList,
    handleScan,
  };
};

export default PDA1121_R01Content;
